import {gsap, Draggable, InertiaPlugin} from "gsap/all";
import $ from "jquery";
import {BaseGameMNS2} from "../base/BaseGameMNS2";
import {DraggableScene, DRAGGABLE_SCENE_POSITIONING, DRAGGABLE_SCENE_LOCK_BEHAVIOUR} from './DraggableScene.js';
import {Timeline} from './Timeline.js';
import {Starfield} from './Starfield.js';
import {EarthLevel1} from "./EarthLevel1";
import {ContextualMenu} from "./ContextualMenu";
import {VideoPlayer} from "./VideoPlayer";
import {AdditionalFloatingStars} from "./AdditionalFloatingStars";
import {EarthLevel2} from "./EarthLevel2";
import {AnimationScene} from "./level3/AnimationScene";

// Constructor
export class EtoilesApp extends BaseGameMNS2 {

  constructor() {
    super(`data/etoiles.json`);
    gsap.registerPlugin(Draggable)
    gsap.registerPlugin(InertiaPlugin)

    this.currentResults = [];

    this.scenes = {
      earthH2O: null,
      objectsStars: null,
      objectsMolecules: null,
      level3Scene: null,
    }
  }

  // To be overridden in child games
  initGame() {
    // Seve final screen ID
    this.seveCtaScreenID = 5;
    this.seveCommunication.on("buttonPressed", (e) => this.onButtonPressed(e));

    const timestampEvents = [{
      timestamp: 0,
      callback: () => this.timeline.highlight(1)
    }, {
      timestamp: 5.6,
      callback: () => gsap.to("#language-switcher, #additional-tools, #etoiles-timeline", {alpha: 0})
    }];
    this.videoPlayer = new VideoPlayer("#screen-2-video-player", timestampEvents, () => this.goToScreen(3));
    this.videoPlayerLevel3 = new VideoPlayer("#screen-2-video-player-p3", timestampEvents, () => this.goToScreen(3));

    this.starfield = new Starfield("#starfield");

    this.timeline = new Timeline("#etoiles-timeline")
    this.timeline.highlight(0)

    this.earthLevel1 = new EarthLevel1("#earth-level-1");
    this.earthLevel2 = new EarthLevel2("#earth-level-2");

    // Screen 3 - Level 2
    this.scenes.earthH2O = new DraggableScene({
      selector: "#draggable-scene-earth-h2o",
      positionMode: DRAGGABLE_SCENE_POSITIONING.LEFT_RIGHT,
      elementsCountByDropzone: 1,
      maxAttempts: 3,
      lockBehavior: DRAGGABLE_SCENE_LOCK_BEHAVIOUR.VISIBLE,
      dropzoneCompletedCallback: (dropzone, scene) => {
      },
      allCompletedCallback: () => this.level2ValidateH2O(),
    });

    // Screen 3 - Level 3
    this.scenes.level3Scene = new AnimationScene({
      selector: "#animation-scene-level-3",
      data: this.data.content.screen_3.level3,
      allCompletedCallback: () => this.level3CellPopup(),
    });

    // ---

    // Screen 4 - Level 1
    this.scenes.objectsStars = new DraggableScene({
      selector: "#draggable-scene-objects-stars",
      positionMode: DRAGGABLE_SCENE_POSITIONING.STARFIELD,
      elementsCountByDropzone: 3,
      dropzoneProgressCallback: this.objectMoleculesDropzoneProgress.bind(this),
      dropzoneCompletedCallback: this.level1ObjectMoleculesCompletedAnimation.bind(this),
      allCompletedCallback: () => this.objectsShowCompletePopup(),
    });

    // Screen 4 - Level 2
    this.scenes.objectsMolecules = new DraggableScene({
      selector: "#draggable-scene-objects-molecules",
      positionMode: DRAGGABLE_SCENE_POSITIONING.LEVEL2,
      elementsCountByDropzone: 3,
      grabRadius: 200,
      lockBehavior: DRAGGABLE_SCENE_LOCK_BEHAVIOUR.VISIBLE_FLOAT,
      dropzoneProgressCallback: this.objectMoleculesDropzoneProgress.bind(this),
      dropzoneCompletedCallback: this.level2ObjectMoleculesCompletedAnimation.bind(this),
      allCompletedCallback: () => this.objectsShowCompletePopup(),
    });

    super.initGame();
    this.additionalFloatingStars = {
      "level2": new AdditionalFloatingStars("#screen-4-LEVEL2 .additional-floating-stars"),
      "level3": new AdditionalFloatingStars("#screen-4-LEVEL3 .additional-floating-stars")
    };

    // TODO: For debugging
    // this.level = "level3"
    // super.initGame(3)
    // setTimeout(() => super.initGame(3), 400);
    // this.earth.bringInBigEarth()
    // setTimeout(() => this.objectsShowCompletePopup(3), 1400);
  }


  goToScreen(screenId, callback = null, skipLockScreenCheck = false) {
    if (screenId === 3 && this.level === "level3") gsap.set("#level-3-transition-overlay", {autoAlpha: 1})
    super.goToScreen(screenId, callback, skipLockScreenCheck);
  }

  // To be overridden in child games
  initScreen(screenId) {
    super.lottieAutoPlaybackForScreen(screenId);

    const screenIdNoLevel = parseInt(screenId.toString().replace("-" + this.level, ""));
    console.log("initScreen ids:", {screenIdNoLevel: screenIdNoLevel, screenId: screenId});

    // Handle additional tools visibility
    if (screenIdNoLevel === 1 || screenIdNoLevel === this.seveCtaScreenID) this.setAdditionalToolsVisibility(false, false)
    else if (screenIdNoLevel === 3) setTimeout(() => this.setAdditionalToolsVisibility(true, true), 1200);
    else this.setAdditionalToolsVisibility(true, true);

    // Disable additional floating stars
    for (let level in (this.additionalFloatingStars)) {
      this.additionalFloatingStars[level].stop();
    }

    if (screenIdNoLevel === 1) {
      this.seveCommunication.sendMessage(0);
      this.timeline.hide();
      this.starfield.showFlare();

    } else if (screenIdNoLevel === 2) {
      this.seveCommunication.sendMessage(1);
      this.timeline.show();

      if (this.level === "level3") {
        this.audio.play("audio/MNS2_Int1.2 Poussiere cosmique_SFX/MNS2_ME_INT1.2_SFXintro3.mp3", 1, true)
        this.videoPlayerLevel3.play();
      } else {
        this.audio.play("audio/MNS2_Int1.2 Poussiere cosmique_SFX/MNS2_ME_INT1.2_SFXintro.mp3", 1, true)
        this.videoPlayer.play();
      }

    } else if (screenIdNoLevel === 3) {
      this.starfield.showFlare();

      // For level 1, show `earthStars` on autoplay. Otherwise, use earthMolecules
      if (this.level === "level1") {
        const tl = this.earthLevel1.bringInBigEarth();
        tl.add(() => this.goToScreen(4), "-=0.5");

      } else if (this.level === "level2") {
        // Interactive mode
        const draggableTarget = this.scenes.earthH2O;
        draggableTarget.reset().animateIn(false);
        this.earthLevel2.bringInitialEarth(draggableTarget.enable.bind(draggableTarget));

        // Update timeline
        this.timeline.highlight(3)

      } else if (this.level === "level3") {
        // Interactive mode
        this.starfield.hideStars();
        const scene = this.scenes.level3Scene;
        scene.reset().animateIn(false);
        // this.earthLevel1.bringInFromStars(scene.enable.bind(scene));

        // Prevent triggering of still there popup
        this.resetTimer()

        // Update timeline
        this.timeline.highlight(4)
      }

    } else if (screenIdNoLevel === 4) {
      this.timeline.show().highlight(5);
      this.starfield.hideFlare();
      this.starfield.hideStars(["level1", "level3"].includes(this.level));

      if (this.level === "level1") {
        if (!this.earthLevel1.isBigEarthShown()) this.earthLevel1.bringInBigEarth();
        this.getObjectsDraggableSceneForLevel().reset().animateIn(true);

      } else if (this.level === "level2") {
        this.getObjectsDraggableSceneForLevel().reset().animateIn(true);
        this.scenes.objectsMolecules.level2DropzoneUnlock(0, false);
        this.scenes.objectsMolecules.$dropZonesEl.eq(0).removeClass("locked");
        this.additionalFloatingStars[this.level].start();

      } else if (this.level === "level3") {
        this.currentTimeline = gsap.timeline();

        // Play sound track
        app.audio.play("audio/MNS2_Int1.2 Poussiere cosmique_SFX/MNS2_ME_INT1.2_SFXfin3.mp3", 1, true)

        // Play main animation
        this.currentTimeline.add(() => {
          const $el = $(`#screen-4-LEVEL3 > .lottie-animation`);
          const lottie = $el.data('lottie');
          lottie.setFrame(0)
          lottie.play();
        })

        // Remove transition overlay
        this.currentTimeline.add(() => {
          gsap.to("#level-3-transition-overlay", {autoAlpha: 0, duration: 0.5})
          this.starfield.hideStars(false);
        }, 0.35)

        // Show life forms at specific time
        this.currentTimeline.add(() => {
          this.level3ShowLife();
        }, 11.5)
      }

    } else if (screenIdNoLevel === 5) {
      this.seveCommunication.sendMessage(3);
      this.audio.play("audio/MNS2_Communs_SFX/MNS2_ME_SFXpartage.mp3", 1, true)
      this.seveVideoPlayback("cta")
      // Auto quit app
      this.currentTimeline = gsap.timeline();
      this.currentTimeline.add(() => {
        // Avoid earth being shown after Seve screen
        this.earthLevel1.reset();
        this.earthLevel2.reset();
      }, 4)
      this.currentTimeline.add(() => this.reset(), 14)
    }

    const topTooltipDelay = (screenId === "3-LEVEL3") ? 3 : 0;
    this.showScreenTopTooltip(screenId, topTooltipDelay);
  }

  draggableSceneEarthEndAnimation(dropzone, scene) {
    const tl = gsap.timeline();
    tl.add(() => scene.disable().attractAll(960, 540));
    tl.add(this.earthLevel1.contractAndExplode());
    tl.to($(`.dropzone-progress`, scene.$el), {autoAlpha: 0, duration: 0.2, ease: "sine.in"}, 2.65);
    return tl;
  }

  objectMoleculesDropzoneProgress($dropZoneEl) {
    const scene = this.getObjectsDraggableSceneForLevel();
    this.setProgressClasses(scene, $dropZoneEl);
  }

  //  Toggle progress classes (count)
  setProgressClasses(scene, $dropZoneEl) {
    const dropzoneIndex = $dropZoneEl.data("index");
    const completedCount = scene.getProgressCount(dropzoneIndex);
    $(`#dropzone-progress-${dropzoneIndex + 1}`, scene.$el).removeClass("count-1 count-2 count-3 count-4 count-5")
    $(`#dropzone-progress-${dropzoneIndex + 1}`, scene.$el).addClass(`count-${completedCount}`)
  }

  level1ObjectMoleculesCompletedAnimation($dropZoneEl) {
    const tl = gsap.timeline();
    tl.to($dropZoneEl, {
      scale: 0,
      duration: 0.3,
      ease: "back.in(1.7)",
    })

    // Hide progression elements
    const progressElementIndex = parseInt($dropZoneEl.data("index")) + 1;
    const progressElement = $(`#dropzone-progress-${progressElementIndex}`, this.getObjectsDraggableSceneForLevel().$el)
    tl.to(progressElement, {autoAlpha: 0, scale: 0.8, duration: 0.3, ease: "back.in(1.7)",}, '<');

    // Flip CTA image and result image
    tl.set($(".cta-image", $dropZoneEl), {autoAlpha: 0});
    tl.set($(".result-image", $dropZoneEl), {autoAlpha: 1});

    tl.add(() => app.audio.play("audio/MNS2_Int1.2 Poussiere cosmique_SFX/MNS2_ME_INT1.2_SFXreveal.mp3", 1, true), "-=0.1")

    tl.add(() => {
      const eclatLottie = $("canvas.eclat", $dropZoneEl).data("lottie")
      eclatLottie.setFrame(0)
      eclatLottie.play();
      console.log("eclatLottie")
    }, "-=0.2")

    // Set result image on "flip"
    const allAvailableResults = ["ver", "Abeille", "Champignon", "Chevreuil", "Coyote", "Grenouille", "humain", "Ours_noir", "Pissenlit", "Pommier", "Raton", "truite", "Arbuste", "Bouquet_fleurs", "Quenouille", "Sapin"];
    const notAlreadyShownResults = allAvailableResults.filter(item => !this.currentResults.includes(item));
    const result = notAlreadyShownResults[Math.floor(Math.random() * notAlreadyShownResults.length)];
    this.currentResults.push(result);
    tl.set($(".result-image", $dropZoneEl), {background: `url("./images/etoiles/results/${result}.png") center center no-repeat`});
    tl.set($(".result-image", $dropZoneEl), {backgroundSize: "contain"});

    tl.to($dropZoneEl, {
      scale: 1,
      duration: 0.3,
      ease: "power1.out",
    })

    return tl;
  }

  level2ObjectMoleculesCompletedAnimation($dropZoneEl) {
    const tl = gsap.timeline();
    const index = $dropZoneEl.data("index");

    // Bring out molecules
    tl.add(this.scenes.objectsMolecules.shrinkShrinkable($dropZoneEl))

    tl.to($dropZoneEl, {
      scale: 0,
      duration: 0.3,
      ease: "back.in(1.7)",
    })

    tl.add(() => app.audio.play("audio/MNS2_Int1.2 Poussiere cosmique_SFX/MNS2_ME_INT1.2_SFXreveal.mp3", 1, true), "-=0.1")

    // Flip CTA image and result image
    tl.set($(".cta-image", $dropZoneEl), {autoAlpha: 0});
    tl.set($(".result-image", $dropZoneEl), {autoAlpha: 1});

    // Lottie animation
    const lottie = $(".result-image canvas", $dropZoneEl).eq(0).data("lottie")
    lottie.setFrame(0)
    lottie.play();

    tl.add(() => {
      const eclatLottie = $(".result-image canvas.eclat", $dropZoneEl).data("lottie")
      eclatLottie.setFrame(0)
      eclatLottie.play();
    }, "-=0.4")

    tl.to($dropZoneEl, {
      scale: 1,
      duration: 0.3,
      ease: "power1.out",
    })

    // Unlock next dropzone
    tl.add(() => {
      if (index === 0) this.scenes.objectsMolecules.level2DropzoneUnlock(1)
      else if (index === 1) this.scenes.objectsMolecules.level2DropzoneUnlock(2)
    })

    // Make draggable usable again
    $(".shrinkable").each((i, n) => {
      const initialPosition = $(n).data("initial-position");
      tl.set(n, {
        x: initialPosition.x,
        y: initialPosition.y,
      }, '<');
      tl.to(n, {
        scale: 1,
        opacity: 1,
        duration: 0.3,
        ease: "sine.out",
      }, '<');
      tl.add(() => {
        $(n).data("locked-dropzone", null);
        this.scenes.objectsMolecules.floatElement(n)
        Draggable.get(n).enable();
        $(n).removeClass("shrinkable");
      }, '<');
    });

    return tl;
  }

  objectMoleculesReset($dropZoneEl) {
    const tl = gsap.timeline();
    tl.to($dropZoneEl, {
      scale: 0,
      duration: 0.3,
      ease: "back.in(1.7)",
    })
    tl.set($(".cta-image", $dropZoneEl), {autoAlpha: 1});
    tl.set($(".result-image", $dropZoneEl), {autoAlpha: 0});
    tl.set($(".result-image", $dropZoneEl), {background: ""});
    tl.to($dropZoneEl, {
      scale: 1,
      duration: 0.3,
      ease: "power1.out",
    })
    return tl;
  }

  moleculeSelecteFromContextualMenu(molecule, $el) {
    // Push elements around point
    const elRect = $el.get(0).getBoundingClientRect();
    this.getObjectsDraggableSceneForLevel().pushAtPoint(elRect.x + elRect.width / 2, elRect.y + elRect.height / 2, 150)
  }

  objectsShowCompletePopup() {
    const killTweens = (this.level !== "level2")
    this.getObjectsDraggableSceneForLevel().disable(killTweens);

    // Additional delay before this fonction
    const tl = gsap.timeline();
    tl.fromTo(`#screen-${this.currentScreen} .finale-wrapper`, {
      autoAlpha: 0,
    }, {
      autoAlpha: 1,
      duration: 0.3,
      ease: "power1.in",
    }, 2);

    tl.fromTo(`#screen-${this.currentScreen} .finale-wrapper .modal`, {
      scale: 0.8,
      autoAlpha: 0,
    }, {
      scale: 1,
      autoAlpha: 1,
      duration: 0.3,
      ease: "power1.in",
    }, 2);

    return tl;
  }

  restartObjects(hardReset = false) {

    const scene = this.getObjectsDraggableSceneForLevel();

    const tl = gsap.timeline();

    // Hide finale wrapper
    if ($(`#screen-${this.currentScreen} .finale-wrapper`).length) {
      tl.to(`#screen-${this.currentScreen} .finale-wrapper`, {
        autoAlpha: 0,
        duration: 0.3,
        ease: "power1.in",
      });

      tl.to(`#screen-${this.currentScreen} .finale-wrapper .modal`, {
        scale: 0.8,
        duration: 0.3,
        ease: "power1.in",
      });
    }

    // Restart objects
    scene.$dropZonesEl.each((i, n) => {
      if ($(n).hasClass("complete")) {
        tl.add(this.objectMoleculesReset(n), "-=0.3")
        tl.add(() => $(n).removeClass("complete"));
      }
      $(`#screen-${this.currentScreen} .dropzone-progress`).removeClass("count-1 count-2 count-3 count-4-count-5")
    })

    // Show progression elements
    tl.to(".dropzone-progress", {autoAlpha: 1, scale: 1, duration: 0.3, ease: "sine.in)",}, '<');

    for (let dropZoneContent of scene.currentDropZoneContent) {
      scene.currentDropZoneContent[dropZoneContent.index].content = [];
    }

    if (this.level === "level2") {
      scene.level2DropzoneUnlock(0)
      scene.level2DropzoneLock(1)
      scene.level2DropzoneLock(2)
    }

    // Only re-enable draggable if it is a user initiated restart
    if (!hardReset) {
      tl.add(() => this.getObjectsDraggableSceneForLevel().enable())
    }
  }

  async level2ValidateH2O() {

    const tl = gsap.timeline();

    // Validate molecules
    const slotsValidation = [
      (this.scenes.earthH2O.currentDropZoneContent[0].content[0] === 'h'),
      (this.scenes.earthH2O.currentDropZoneContent[1].content[0] === 'o'),
      (this.scenes.earthH2O.currentDropZoneContent[2].content[0] === 'h'),
    ];

    // Handle attempts
    this.scenes.earthH2O.attempts++;
    const isLastAttempt = this.scenes.earthH2O.attempts >= this.scenes.earthH2O.maxAttempts;

    const hideAutocomplete = {
      scale: 0.3,
      opacity: 0,
      duration: 1.4,
      stagger: 0.2,
      ease: "back.out(1.7)",
    }

    // Disable drag if end is reached
    if (!slotsValidation.includes(false) || isLastAttempt) {
      tl.add(this.scenes.earthH2O.disable(), 0)
    }

    // Add feedback
    for (let i = 0; i < slotsValidation.length; i++) {
      const target = slotsValidation[i] ? "good" : "wrong";
      const lottie = this.scenes.earthH2O.$dropZonesEl.eq(i).find(`canvas.${target}`).data('lottie');
      gsap.killTweensOf(lottie.canvas);
      gsap.set(lottie.canvas, {autoAlpha: 1});

      tl.add(() => {
        this.timeline.highlight(4)
        lottie.setFrame(0);
        lottie.play();
      })
    }

    // Final action
    if (slotsValidation.includes(false)) {
      // Is wrong
      tl.add(this.scenes.earthH2O.moveBackToInitialPosition(slotsValidation));
      app.audio.play("audio/MNS2_Int1.2 Poussiere cosmique_SFX/MNS2_ME_INT1.2_SFXH2Obad.mp3", 1, true)
      //  Show failure popup
      if (!isLastAttempt) tl.add(() => this.toggleTooltip(true, "level2_screen3_fail"), "-=1.5");
      // Auto complete H2O when max attempt is reached
      else tl.add(this.autoCompleteEarthH2O(slotsValidation));
    } else {
      app.audio.play("audio/MNS2_Int1.2 Poussiere cosmique_SFX/MNS2_ME_INT1.2_SFXH2Ogood.mp3", 1, true)
      // Success, add time to breath
      tl.add(() => {
      }, "+=2.0");
    }

    if (!slotsValidation.includes(false) || isLastAttempt) {
      tl.add(async () => {
        app.audio.play("audio/MNS2_Int1.2 Poussiere cosmique_SFX/MNS2_ME_INT1.2_SFXeauterre.mp3", 1, true)
        await this.earthLevel2.addWaterToEarth();
        await this.toggleTooltip(true, "level2_screen3_success")
        app.audio.play("audio/MNS2_Int1.2 Poussiere cosmique_SFX/MNS2_ME_INT1.2_SFXcontinent.mp3", 1, true)
        await this.earthLevel2.developEarthWithWater();
        this.goToScreen(4);
      })
      // Hide draggable particles
      tl.add(this.scenes.earthH2O.animateOut())// Evolve earth
      // Hide autocomplete particles
      if (!slotsValidation[0]) tl.to("#solution-A", hideAutocomplete, '>-1.7');
      if (!slotsValidation[2]) tl.to("#solution-B", hideAutocomplete, '>-1.7');
      if (!slotsValidation[1]) tl.to("#solution-C", hideAutocomplete, '>-1.7');
      // Hide dropzone
      tl.fromTo($("#screen-3-LEVEL2 .dropzones"), {scale: 1, autoAlpha: 1}, {autoAlpha: 0, scale: 0.8, duration: 0.4}, "-=1.4");
    }

    this.currentTimeline = tl;
  }

  autoCompleteEarthH2O(slotsValidation) {
    const tl = gsap.timeline();
    const from = {autoAlpha: 0, scale: 0.6};
    const to = {autoAlpha: 1, scale: 1, ease: "back.out"};

    if (!slotsValidation[0]) tl.fromTo("#solution-A", from, to, "<>-0.15");
    if (!slotsValidation[2]) tl.fromTo("#solution-B", from, to, "<>-0.15");
    if (!slotsValidation[1]) tl.fromTo("#solution-C", from, to, "<>-0.15");

    return tl;
  }

  level3GoToStep(stepIndex) {
    this.closeTooltip();
    this.scenes.level3Scene.goToStep(stepIndex);
  }

  async level3CellPopup() {
    await this.toggleTooltip(true, "level3_step5_b")
    this.level3ToFinaleEarth()
  }

  async level3ToFinaleEarth() {
    await this.scenes.level3Scene.animateOut();
    this.goToScreen(4)
  }

  level3ShowLife() {
    this.currentTimeline = gsap.timeline();

    for (let i = 1; i <= 3; i++) {

      // Eclat
      this.currentTimeline.add(() => {
        gsap.set(`#level3-life-element-${i}`, {autoAlpha: 1});
        const $el = $(`#level3-life-element-${i} .lottie-animation.eclat`);
        const lottie = $el.data('lottie');
        lottie.setFrame(0)
        lottie.play();
      }, ((i - 1) * 1.75));

      // Flare sound effect
      if (i !== 1) this.currentTimeline.add(() => app.audio.play("audio/MNS2_Int1.2 Poussiere cosmique_SFX/MNS2_ME_INT1.2_SFXreveal.mp3", 1, true), "-=0.1")

      // Life
      this.currentTimeline.fromTo($(`#level3-life-element-${i} .lottie-animation.life`), {scale: 0.5, autoAlpha: 0}, {scale: 1, autoAlpha: 1, ease: "back.out"}, "+=0.1");
      this.currentTimeline.add(() => {
        const $el = $(`#level3-life-element-${i} .lottie-animation.life`);
        const lottie = $el.data('lottie');
        lottie.setFrame(0)
        lottie.play();
      });
    }

    // Tooltip
    this.currentTimeline.add(() => {
      this.toggleTooltip(true, "level3_screen4").then(() => {
        this.starfield.hideStars()
        this.goToScreen(5)
      });
    }, 6);

  }

  getObjectsDraggableSceneForLevel() {
    if (this.level === "level1") return this.scenes.objectsStars;
    if (this.level === "level2") return this.scenes.objectsMolecules;
    if (this.level === "level3") return this.scenes.level3Scene;
  }

  setAdditionalToolsVisibility(showHome = true, showHelp = false) {
    if (showHome || showHelp) gsap.to("#language-switcher, #additional-tools, #etoiles-timeline", {alpha: 1})
    super.setAdditionalToolsVisibility(showHome, showHelp);
    if (showHome) this.timeline.show();
    else this.timeline.hide();
  }

  goToSeve() {
    const tl = gsap.timeline();
    tl.to(`#screen-${this.currentScreen} .finale-wrapper .modal`, {
      scale: 0.8,
      autoAlpha: 0,
      duration: 0.3,
      ease: "power1.in",
    });
    tl.add(() => this.goToScreen(5), 0.2);
  }

  updateSeveVideosSrc(language) {
    super.updateSeveVideosSrc(language,
      "videos/seve_A/ZONE1_ME_MNS2_INTNUM_ENVOIE_SEVE_{{LANG}}.mp4",
      "videos/seve_B/ZONE1_ME_MNS2_INTNUM_SEVE_ENVOYE_{{LANG}}.mp4"
    );
  }

  seveVideoPlayback(state) {
    super.seveVideoPlayback(state);
    if (state === "result") {
      this.earthLevel1.reset();
      this.earthLevel2.reset();
    }
  }

  // Double the screensaver delay for level 3
  getScreenSaverDelay() {
    return (this.level === "level3" && this.screensaverDelay !== -1 && this.screensaverDelay > 1) ? (this.screensaverDelay * 2) : this.screensaverDelay;
  }

  /**
   * Reset function, used to go back clean version of the game
   * @param screenId
   */
  reset(screenId) {
    // TODO: Reset things here

    // Reset redirect timeout
    if (this.redirectTimeout !== false) {
      clearTimeout(this.redirectTimeout);
      this.redirectTimeout = false;
    }

    // Reset variable and timeline
    this.killTimeline(this.currentTimeline);

    // Reset elements
    this.timeline.hide(true);

    this.earthLevel2.hide();

    const resetCallback = () => {
      // Things that reset after goToScreen
      this.seveVideoPlayback("none")

      this.earthLevel1.reset();
      this.earthLevel2.reset();
      this.videoPlayer.reset();
      this.videoPlayerLevel3.reset();
      this.restartObjects();
      this.starfield.hideStars(false);
      this.currentResults = [];

      this.closeTooltip();

      Object.keys(this.scenes).forEach((key) => {
        this.scenes[key].reset();
        this.scenes[key].disable();
        this.scenes[key].$dropZonesEl?.find(".lottie-animation").each((i, n) => {
          const lottie = $(n).data('lottie');
          lottie.stop()
          lottie.setFrame(0)
        })
      })

      gsap.set(".dropzone-progress", {autoAlpha: 1, scale: 1}, '<');
      gsap.set(`.finale-wrapper`, {autoAlpha: 0});
      gsap.set("#level-3-transition-overlay", {autoAlpha: 0})
      gsap.set(`#solution-A, #solution-B, #solution-C`, {autoAlpha: 0});

      // Level 2 reset draggable state
      this.scenes.objectsMolecules.$dropZonesEl.addClass("locked");
      for (let i = 0; i < this.scenes.objectsMolecules.$dropZonesEl; i++) {
        this.scenes.objectsMolecules.level2DropzoneLock(i);
      }

      this.scenes.objectsMolecules.$dropZonesEl.addClass("locked");
      for (let i = 0; i < this.scenes.objectsMolecules.$dropZonesEl.length; i++) {
        this.scenes.objectsMolecules.level2DropzoneLock(i);
      }

      app.audio.clearAll()

      // Level 3 reset final looping animations
      gsap.set(`#screen-4-LEVEL3 .life-element`, {autoAlpha: 0});
      $(`#screen-4-LEVEL3 .lottie-animation.life`).each((i, n) => {
        const lottie = $(n).data('lottie');
        lottie.stop()
        lottie.setFrame(0)
      })

      // Remove all progress states
      $(".dropzone-progress").removeClass("count-1 count-2 count-3 count-4 count-5")

    }

    // Super
    return super.reset(screenId, resetCallback);

  }

}
